import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import MockDataService from '../../../services/MockDataService';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Textarea } from '../../../components/ui';
import { 
  MessageCircle, Send, Paperclip, Download, User, Clock, CheckCircle, 
  AlertCircle, RefreshCw, Search, FileText, Bell,
  Image, File, X, Eye, EyeOff
} from 'lucide-react';

const TicketCommunication = () => {
  const { ticketId } = useParams();
  const [communicationData, setCommunicationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  const loadCommunicationData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Validate ticketId - must be a valid number
      // Check if ticketId is a reserved route name (like "my-tickets", "create", "new", "attachments", "feedback")
      const reservedRoutes = ['my-tickets', 'create', 'new', 'attachments', 'feedback'];
      if (reservedRoutes.includes(ticketId)) {
        setError(`Invalid ticket ID: "${ticketId}" is a reserved route name`);
        setLoading(false);
        return;
      }
      
      // Check if ticketId is a valid number
      const ticketIdNum = Number(ticketId);
      if (isNaN(ticketIdNum) || ticketIdNum <= 0) {
        setError(`Invalid ticket ID: "${ticketId}" is not a valid ticket number`);
        setLoading(false);
        return;
      }
      
      const response = await MockDataService.getTicketCommunication(ticketId);
      if (response.success) {
        setCommunicationData(response.data);
      } else {
        setError('Failed to load communication data');
      }
    } catch (err) {
      setError('Error loading communication data');
      console.error('Error loading communication:', err);
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    if (ticketId) {
      loadCommunicationData();
    }
  }, [ticketId, loadCommunicationData]);

  const getCommunicationTypeIcon = (type) => {
    switch (type) {
      case 'system': return <AlertCircle className="h-4 w-4 text-blue-500" />;
      case 'agent': return <User className="h-4 w-4 text-green-500" />;
      case 'customer': return <MessageCircle className="h-4 w-4 text-primary-500" />;
      default: return <MessageCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getCommunicationTypeColor = (type) => {
    switch (type) {
      case 'system': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'agent': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'customer': return 'bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'delivered': return <Eye className="h-4 w-4 text-blue-500" />;
      case 'read': return <EyeOff className="h-4 w-4 text-gray-500" />;
      default: return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays}d ago`;
    }
  };

  const handleSendMessage = () => {
    if (!newMessage.trim()) return;
    
    // Simulate sending message
    alert(`Message sent: "${newMessage}"`);
    setNewMessage('');
    setAttachments([]);
    setSelectedTemplate('');
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template.id);
    setNewMessage(template.content);
    setShowTemplates(false);
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    if (attachments.length + files.length > communicationData?.attachmentSettings?.maxFiles) {
      alert(`Maximum ${communicationData?.attachmentSettings?.maxFiles} files allowed`);
      return;
    }
    setAttachments([...attachments, ...files]);
  };

  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const filteredCommunications = () => {
    if (!communicationData?.ticketCommunications) return [];
    
    let filtered = communicationData.ticketCommunications;
    
    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter(comm => comm.type === filterType);
    }
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(comm => 
        comm.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        comm.author.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return filtered;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-text-secondary-light dark:text-text-secondary-dark">Loading communication...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600 dark:text-red-400">{error}</p>
        <Button onClick={loadCommunicationData} className="mt-4">Retry</Button>
      </div>
    );
  }

  if (!communicationData) {
    return (
      <div className="text-center py-8">
        <p className="text-text-secondary-light dark:text-text-secondary-dark">No communication data available</p>
      </div>
    );
  }

  const communications = filteredCommunications();
  const stats = communicationData.communicationStats;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark">
            Ticket Communication
          </h1>
          <p className="text-text-secondary-light dark:text-text-secondary-dark">
            Communicate with support agents about your ticket
          </p>
        </div>
        <div className="flex space-x-3">
          <Button variant="outline" onClick={() => setShowNotifications(!showNotifications)}>
            <Bell size={18} />
            <span>Notifications</span>
          </Button>
          <Button variant="outline" onClick={loadCommunicationData}>
            <RefreshCw size={18} />
            <span>Refresh</span>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Total Messages</p>
                <p className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">{stats.totalCommunications}</p>
              </div>
              <MessageCircle className="h-8 w-8 text-primary-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Avg Response Time</p>
                <p className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">{stats.averageResponseTime}h</p>
              </div>
              <Clock className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Satisfaction</p>
                <p className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">{stats.communicationSatisfaction}/5</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Template Usage</p>
                <p className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">{stats.templateUsage}%</p>
              </div>
              <FileText className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-2">
              <Search className="h-4 w-4 text-text-secondary-light dark:text-text-secondary-dark" />
              <input
                type="text"
                placeholder="Search messages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-3 py-2 border border-border-light dark:border-border-dark rounded-md bg-surface-light dark:bg-surface-dark text-text-primary-light dark:text-text-primary-dark focus:ring-primary-500 focus:border-primary-500 w-full"
              />
            </div>
            <select 
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border border-border-light dark:border-border-dark rounded-md bg-surface-light dark:bg-surface-dark text-text-primary-light dark:text-text-primary-dark focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Messages</option>
              <option value="customer">My Messages</option>
              <option value="agent">Agent Messages</option>
              <option value="system">System Messages</option>
            </select>
            <Button variant="outline" onClick={() => setShowTemplates(!showTemplates)}>
              <FileText size={18} />
              <span>Templates</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Communication History */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <MessageCircle className="h-5 w-5" />
                <span>Communication History ({communications.length})</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {communications.map((comm) => (
                  <div key={comm.id} className="border border-border-light dark:border-border-dark rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        {getCommunicationTypeIcon(comm.type)}
                        <span className="font-medium text-text-primary-light dark:text-text-primary-dark">
                          {comm.author.name}
                        </span>
                        <Badge className={getCommunicationTypeColor(comm.type)}>
                          {comm.type}
                        </Badge>
                        {comm.template && (
                          <Badge variant="outline" className="text-xs">
                            {comm.template}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(comm.status)}
                        <span className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                          {formatDateTime(comm.timestamp)}
                        </span>
                      </div>
                    </div>
                    <p className="text-text-primary-light dark:text-text-primary-dark mb-3">
                      {comm.content}
                    </p>
                    {comm.attachments && comm.attachments.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">Attachments:</p>
                        <div className="flex flex-wrap gap-2">
                          {comm.attachments.map((attachment) => (
                            <div key={attachment.id} className="flex items-center space-x-2 p-2 bg-gray-50 dark:bg-gray-800 rounded">
                              {attachment.type.startsWith('image/') && <Image className="h-4 w-4" />}
                              {attachment.type === 'application/pdf' && <FileText className="h-4 w-4" />}
                              {!attachment.type.startsWith('image/') && attachment.type !== 'application/pdf' && <File className="h-4 w-4" />}
                              <span className="text-sm text-text-primary-light dark:text-text-primary-dark">{attachment.name}</span>
                              <Button variant="ghost" size="sm">
                                <Download className="h-3 w-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {communications.length === 0 && (
                  <div className="text-center py-8">
                    <MessageCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-text-secondary-light dark:text-text-secondary-dark">No messages found</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Communication Form & Templates */}
        <div className="lg:col-span-1 space-y-6">
          {/* Templates */}
          {showTemplates && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <FileText className="h-5 w-5" />
                  <span>Communication Templates</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {communicationData.communicationTemplates.map((template) => (
                    <Button
                      key={template.id}
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => handleTemplateSelect(template)}
                    >
                      <span className="text-left">
                        <div className="font-medium">{template.name}</div>
                        <div className="text-xs text-text-secondary-light dark:text-text-secondary-dark">{template.category}</div>
                      </span>
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* New Message Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Send className="h-5 w-5" />
                <span>Send Message</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-primary-light dark:text-text-primary-dark mb-2">
                  Message
                </label>
                <Textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type your message here..."
                  className="min-h-[100px]"
                />
              </div>

              {/* Attachments */}
              <div>
                <label className="block text-sm font-medium text-text-primary-light dark:text-text-primary-dark mb-2">
                  Attachments
                </label>
                <div className="space-y-2">
                  <input
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    className="hidden"
                    id="file-upload"
                    accept={communicationData.attachmentSettings.allowedTypes.join(',')}
                  />
                  <label htmlFor="file-upload">
                    <Button variant="outline" className="w-full">
                      <Paperclip className="h-4 w-4 mr-2" />
                      Choose Files
                    </Button>
                  </label>
                  {attachments.length > 0 && (
                    <div className="space-y-1">
                      {attachments.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
                          <span className="text-sm text-text-primary-light dark:text-text-primary-dark">{file.name}</span>
                          <Button variant="ghost" size="sm" onClick={() => removeAttachment(index)}>
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                    Max {communicationData.attachmentSettings.maxFiles} files, {Math.round(communicationData.attachmentSettings.maxFileSize / 1024 / 1024)}MB each
                  </p>
                </div>
              </div>

              <div className="flex space-x-2">
                <Button onClick={handleSendMessage} disabled={!newMessage.trim()}>
                  <Send className="h-4 w-4 mr-2" />
                  Send Message
                </Button>
                <Button variant="outline" onClick={() => setNewMessage('')}>
                  Clear
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Notifications */}
          {showNotifications && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Bell className="h-5 w-5" />
                  <span>Notification Settings</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {communicationData.communicationNotifications.map((notif) => (
                    <div key={notif.id} className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-text-primary-light dark:text-text-primary-dark capitalize">
                          {notif.type} Notifications
                        </p>
                        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                          {notif.settings.frequency} • {notif.enabled ? 'Enabled' : 'Disabled'}
                        </p>
                      </div>
                      <Button variant={notif.enabled ? "default" : "outline"} size="sm">
                        {notif.enabled ? 'On' : 'Off'}
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default TicketCommunication;
